import React                 from 'react';
import { Observe }           from 'rewire-core';
import {
  WithStyle,
  IActionMenuItem
}                            from 'rewire-ui';
import classNames            from 'classnames';
import Button                from '@material-ui/core/Button';
import { Theme }             from '@material-ui/core/styles';
import Tooltip               from '@material-ui/core/Tooltip';
import Typography            from '@material-ui/core/Typography';
import AttachMoneyIcon       from '@material-ui/icons/AttachMoney';
import AutorenewIcon         from '@material-ui/icons/Autorenew';
import BlockOutlinedIcon     from '@material-ui/icons/BlockOutlined';
import Breadcrumbs           from '../main-navigation/Breadcrumbs';
import MainContentToolbar    from '../main-navigation/MainContentToolbar';
import EmployeeDialog        from '../schedule/EmployeeDialog';
import employeeDialogModel   from '../schedule/EmployeeDialogModel';
import ScheduleControls      from '../schedule/ScheduleControls';
import { confirmationModel } from '../ui/ConfirmationDialog';
import MainDateRangeNav      from '../ui/MainDateRangeNav';
import MenuButton            from '../ui/MenuButton';
import { pluckStartEnd }     from '../utility/DateUtils';
import { capitalize }        from '../utility/StringUtils';
import timecardModel         from './TimecardModel';
import { withStyles }        from '../utility/styles';

const styles = (theme: Theme) => ({
  left: {
    display: 'flex',
    flex: '0 1 auto',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: '0 1 auto',
    flexWrap: 'wrap',
  },
  employeeButton: {
    minWidth: '0px',
    minHeight: '0px',
    padding: '0px 5px',
    marginBottom: '7px',
    textTransform: 'none',
    '&:hover': {
      color: theme.palette.dialogPrimary.main,
    },
  },
  controlButton: {
    minWidth: '100px',
    marginLeft: '10px',
    marginBottom: '7px',
  },
  undoButton: {
  },
  saveButton: {
    marginRight: '5px',
    color: theme.palette.success.contrastText,
    backgroundColor: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.light,
    },
  },
  runRulesButton: {
    minWidth: 48,
    width: 48,
    height: 29,
    '& span': {
      margin: 0
    },
    color: theme.palette.warning.contrastText,
    backgroundColor: theme.palette.warning.main,
    '&:hover': {
      backgroundColor: theme.palette.warning.light,
    },
  },
  transitionButton : {
    display: 'inline-flex',
    marginBottom: '7px',
    marginLeft: '10px',
  },
  batchName: {
    color: '#0060bf', // theme.palette.dialogPrimary.main,
  },
  money: {
    fontSize: '16px',
    color: 'green',
    marginLeft: '6px',
    marginBottom: '7px',
  },
  block: {
    fontSize: '24px',
    color: 'darkred',
    marginLeft: '-20px',
    marginBottom: '7px',
  }
});

interface ITimecardToolbarProps {
  theName?: string;
}

type TimecardToolbarProps = WithStyle<ReturnType<typeof styles>, ITimecardToolbarProps>;

class TimecardToolbar extends React.PureComponent<TimecardToolbarProps> {
  constructor(props: TimecardToolbarProps) {
    super(props);
  }

  get timecardTransitionItems(): IActionMenuItem[] {
    const allowableTransitions = timecardModel.allowableTransitions || [];
    if (!allowableTransitions.length) return [];

    const items: IActionMenuItem[] = [];
    for (const transition of allowableTransitions) {
      items.push({
        name:         transition.event,
        title:        capitalize(transition.event),
        closeOnClick: true,
        disabled:     () => !transition.enabled,
        onClick:      () => timecardModel.executeTransition(transition.event),
      });
    }
    return items;
  }

  handleEmployeeClick = () => {
    employeeDialogModel.load(timecardModel.selectedEmployee?.id);
  };

  handleUndo = () => {
    confirmationModel.load(
      `Please confirm you wish to undo the changes you have made to the timecard for ${timecardModel.selectedEmployee!.name} during ${pluckStartEnd(timecardModel.effective!)}`,
      {
        yesAction: () => {
          timecardModel.cancel();
          return true;
        },
        noAction: () => true,
        maxWidth: 'sm', title: 'Confirm Cancel Timecard Changes'
      }
    );
  };

  handleRunRules = () => {
    timecardModel.runRules();
  };

  handleSave = () => {
    timecardModel.save();
  };

  render() {
    const { classes } = this.props;

    return (
      <MainContentToolbar>
        <Observe render={() => {
          const name      = timecardModel.selectedEmployee?.name ?? '';
          const isPayable = timecardModel.isPayable;
          const title     = isPayable ? '' : 'not payable';

          return (
            < >
            <div className={classes.left}>
              <Breadcrumbs />
              <MainDateRangeNav />
              <Button className={classes.employeeButton} onClick={this.handleEmployeeClick}><Typography variant='h4' display='inline'>{name}</Typography></Button>
              {!isPayable
                ? <><AttachMoneyIcon className={classes.money} /><Tooltip title={title}><BlockOutlinedIcon className={classes.block} /></Tooltip></>
                : null
              }
            </div>
            <div className={classes.right}>
              <div>
                <Observe render={() => <Button className={classNames(classes.controlButton, classes.runRulesButton)} variant='contained' disabled={!timecardModel.canRunRules()} onClick={this.handleRunRules} title={'Run Rules'} size='small' startIcon={<AutorenewIcon />}></Button>}/>
                <Observe render={() => {
                  const timecardStatus = timecardModel.status;

                  return (
                    (timecardStatus)
                      ? <MenuButton disabled={!timecardModel.canChangeStatus()} text={timecardStatus} items={this.timecardTransitionItems} classes={{button: classNames(classes.controlButton, classes.transitionButton)}} />
                      : null
                  );
                }} />
                <Observe render={() => <Button className={classNames(classes.controlButton, classes.undoButton)} variant='contained' disabled={!timecardModel.canSave()} onClick={this.handleUndo} title='Undo'>Undo</Button>} />
                <Observe render={() => <Button className={classNames(classes.controlButton, classes.saveButton)} variant='contained' disabled={!timecardModel.canSave()} onClick={this.handleSave} title='Save'>Save</Button>} />
              </div>
              <ScheduleControls hideGroupBy={true} hideSortBy={false} />
            </div>
            </>
          );
        }} />
        <EmployeeDialog />
      </MainContentToolbar>
    );
  }
}

export default withStyles<ITimecardToolbarProps>(styles, TimecardToolbar);
